import { isBillNoMatch } from "./billNoMatchUtils";
import { isSyntheticReconRow } from "./reconBillUploadUtils";

export type ReconMismatchRow = {
  cancelled?: boolean;
  documentKind?: string;
  reconId?: number;
  billNo?: string;
  doa?: string;
  dod?: string;
  billAmount?: string;
  aiPatientName?: string;
  aiUhid?: string;
  aiDoa?: string;
  aiDod?: string;
  cghsTotal?: string;
  aiBillNo?: string;
  billNoMatched?: boolean;
};

const normalizeComparable = (value?: string | null) =>
  (value || "")
    .trim()
    .toLowerCase()
    .replace(/^(mr|mrs|ms|miss|dr|smt|shri)\.?\s+/i, "")
    .replace(/\s+/g, " ");

export const isMismatch = (
  aiValue?: string | null,
  expectedValue?: string | null,
) => {
  const ai = normalizeComparable(aiValue);
  const expected = normalizeComparable(expectedValue);
  if (!ai || !expected) {
    return false;
  }
  return ai !== expected;
};

const parseAmount = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

export const isAmountMismatch = (
  aiAmount?: string | null,
  billAmount?: string | null,
) => {
  const ai = parseAmount(aiAmount);
  const bill = parseAmount(billAmount);
  if (ai == null || bill == null) {
    return false;
  }
  return Math.abs(ai - bill) > 0.01;
};

const normalizeDateKey = (value?: string | null) => {
  if (!value) {
    return "";
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return "";
  }
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }
  const dmyMatch = trimmed.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{2,4})/);
  if (dmyMatch) {
    const day = dmyMatch[1].padStart(2, "0");
    const month = dmyMatch[2].padStart(2, "0");
    const year =
      dmyMatch[3].length === 2 ? `20${dmyMatch[3]}` : dmyMatch[3];
    return `${year}-${month}-${day}`;
  }
  return trimmed.split(/[T\s]/)[0] || trimmed;
};

export const isDateMismatch = (
  aiValue?: string | null,
  expectedValue?: string | null,
) => {
  const ai = normalizeDateKey(aiValue);
  const expected = normalizeDateKey(expectedValue);
  if (!ai || !expected) {
    return false;
  }
  return ai !== expected;
};

export const isBillNoMismatch = (row: ReconMismatchRow) => {
  if (row.cancelled || isSyntheticReconRow(row)) {
    return false;
  }
  if (row.billNoMatched === false) {
    return true;
  }
  if (row.aiBillNo) {
    return !isBillNoMatch(row.billNo, row.aiBillNo);
  }
  return false;
};

export const rowHasReconMismatch = (
  row: ReconMismatchRow,
  expectedUhid?: string,
  expectedPatientName?: string,
) => {
  if (row.cancelled || isSyntheticReconRow(row)) {
    return false;
  }
  const uhidMismatch = isMismatch(row.aiUhid, expectedUhid);
  const nameMismatch =
    uhidMismatch && isMismatch(row.aiPatientName, expectedPatientName);
  return (
    uhidMismatch ||
    nameMismatch ||
    isDateMismatch(row.aiDoa, row.doa) ||
    isDateMismatch(row.aiDod, row.dod) ||
    isAmountMismatch(row.cghsTotal, row.billAmount) ||
    isBillNoMismatch(row)
  );
};

export const hasAnyReconBillMismatch = (
  rows: ReconMismatchRow[] | undefined,
  expectedUhid?: string,
  expectedPatientName?: string,
) => {
  if (!rows?.length) {
    return false;
  }
  return rows.some((row) =>
    rowHasReconMismatch(row, expectedUhid, expectedPatientName),
  );
};
